import { useMemo } from "react";
import { SLOW_POLL, usePortalDocuments, type PortalDocumentRow } from "../../api/portal";
import { useAuth } from "../../auth/AuthProvider";
import type { Role } from "../../api/types";

const REPROCESS_ROLES: readonly Role[] = ["entity_admin", "finance"];
const ATTENTION_PAGE = 50;

/** The session's role is the pinned membership's role (HUB-E12); undefined until the session has settled. */
export function usePortalRole(): Role | null | undefined {
  const { me, status } = useAuth();
  if (status === "loading") return undefined;
  return me?.role ?? null;
}

export function canReprocess(role: Role | null | undefined): boolean {
  return Boolean(role) && REPROCESS_ROLES.includes(role as Role);
}

/** Inbound rows carry "held", which the outbound DocumentState enum does not name (m4-rulings.md #50). */
export function isHeld(row: { state: string }): boolean {
  return row.state === "held";
}

/** A terminal outbound error is the one thing the portal may restart; inbound holds belong to the central team. */
export function canReprocessRow(
  role: Role | null | undefined,
  row: { direction: string; state: string; errorClass: string | null },
): boolean {
  return canReprocess(role) && row.direction === "out" && row.state === "error" && row.errorClass !== "transient";
}

/** Terminal errors and held inbound documents; a transient error retries on its own. */
export function needsAttention(row: PortalDocumentRow): boolean {
  return (row.state === "error" && row.errorClass !== "transient") || isHeld(row);
}

export function documentName(row: { invoiceNumber?: string | null; erpDocId: string | null; id: string }): string {
  return row.invoiceNumber ?? row.erpDocId ?? row.id;
}

export function useAttention() {
  const errors = usePortalDocuments({ direction: "out", state: "error", limit: ATTENTION_PAGE }, SLOW_POLL);
  const held = usePortalDocuments({ direction: "in", state: "held", limit: ATTENTION_PAGE }, SLOW_POLL);
  const rows = useMemo(
    () =>
      [...(errors.data?.pages ?? []), ...(held.data?.pages ?? [])]
        .flatMap((page) => page.items)
        .filter(needsAttention)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [errors.data, held.data],
  );
  return {
    rows,
    hasMore: Boolean(errors.hasNextPage || held.hasNextPage),
    seeAllTo: overflowLink(Boolean(errors.hasNextPage), Boolean(held.hasNextPage)),
    isError: errors.isError || held.isError,
    isLoading: errors.isLoading || held.isLoading,
  };
}

/** Only the held inbound list overflowing sends "see all" to the held filter; otherwise to the outbound errors. */
export function overflowLink(moreErrors: boolean, moreHeld: boolean): string {
  return moreHeld && !moreErrors ? "/portal/documents?direction=in&state=held" : "/portal/documents?state=error";
}
